'use client';

import { clsx } from 'clsx';

import { useCallback, useState } from 'react';

import { Typography } from '@monoimin/atoms';

import {
  C2ACommonStoryblok,
  C2AMeetingStoryblok,
  C2AFactsheetStoryblok,
} from '@monoimin/sb-types';

import { getBlokVariant } from '../utils';

export interface PreviewDetailsProps extends ComponentCommonProps {
  content: C2ACommonStoryblok | C2AMeetingStoryblok | C2AFactsheetStoryblok;
  slug?: string;
}

export const PreviewDetails = ({
  content,
  isRichText,
  slug,
}: PreviewDetailsProps) => {
  const tooltipDefaultText = 'Click to copy slug';

  const blokVariant = getBlokVariant(content as C2ACommonStoryblok);
  const [tooltipText, setTooltipText] = useState(tooltipDefaultText);

  const handleCopyClickSlug = useCallback(() => {
    navigator.clipboard
      .writeText(slug as string)
      .then(() => {
        setTooltipText('Slug copied to clipboard');
        setTimeout(() => {
          setTooltipText(tooltipDefaultText);
        }, 2000);
      })
      .catch();
  }, [setTooltipText]);

  return (
    <div
      className={clsx('mb-4 flex gap-2 container whitespace-nowrap flex-wrap', {
        '-translate-y-12': content?.variant === 'textBlog' && !isRichText,
        'pl-0': isRichText,
      })}
    >
      <Typography
        className={clsx('caption', {
          '!pl-0': isRichText,
        })}
      >
        Blok type: <b>{`${content?.component}`}</b>
        {`${isRichText ? ' inside richtext' : ''}`}
      </Typography>{' '}
      |
      <Typography
        className={clsx('caption', {
          '!pl-0': isRichText,
        })}
      >
        Variant: <b>{`${blokVariant || 'default'}`}</b>
      </Typography>
      |
      <Typography
        className={clsx('caption relative w-fit', {
          '!pl-0': isRichText,
        })}
      >
        Slug:{' '}
        <b
          onClick={handleCopyClickSlug}
          title={tooltipText}
          className='cursor-pointer hover:underline peer'
        >
          {slug}
        </b>
        <span
          className={clsx(
            'peer-hover:flex absolute left-full ml-5 -top-0.5 min-w-[160px] flex items-center justify-center text-xs border rounded-md border-line  py-1 px-1.5',
            tooltipText === tooltipDefaultText
              ? 'hidden bg-[#FEFCE8]'
              : 'bg-[#E7FCDC]'
          )}
        >
          {tooltipText}
        </span>
      </Typography>
    </div>
  );
};

export default PreviewDetails;
